import { FC, useEffect, useMemo, useState } from "react";
import {
    includesInLC,
    markMatch,
    SelectComponent,
    TextInput,
    toggleInArray,
} from "@datassential/platform-ui-lib";
import { FiSearch } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import cn from "classnames";
import { FaCheck } from "react-icons/fa";
import { CustomBaseScrollbars } from "@components/CustomScrollbarsVirtualList/CustomScrollbarsVirtualList";
import { iAvailableTag } from "@models/GlobalUserData";
import './HomeReportsTagsFilter.scss';

const Toggle: FC<{
    isChecked: boolean;
    onToggle: () => void;
}> = ({ isChecked, onToggle, children }) => (
    <div
        className={cn("HomeReportsTagsFilterToggle", isChecked && 'is-checked')}
        onClick={onToggle}
    >
        <div className="HomeReportsTagsFilterToggle__icon">
            <FaCheck />
        </div>
        <span className="HomeReportsTagsFilterToggle__label">{children}</span>
    </div>
);

interface iProps {
    availableTags: iAvailableTag[];
    selectedTagIds: number[];
    setSelectedTagIds: (ids: number[]) => void;
}

const HomeReportsTagsFilter: FC<iProps> = ({
    availableTags,
    selectedTagIds,
    setSelectedTagIds,
}) => {
    const [isOpened, setIsOpened] = useState(false);
    const [search, setSearch] = useState('');
    const [draftTagIds, setDraftTagIds] = useState<number[]>(selectedTagIds);

    useEffect(() => {
        if (isOpened) {
            setDraftTagIds(selectedTagIds);
        } else {
            setSearch('');
        }
    }, [isOpened]);

    const filteredTags = useMemo(
        () => {
            if (search.trim() === '') return availableTags;
            return availableTags.filter(tag => includesInLC(tag.value, search));
        },
        [availableTags, search]
    );

    const selectedTags = useMemo(
        () => availableTags.filter(tag => selectedTagIds.includes(tag.id)),
        [availableTags, selectedTagIds]
    );

    const handleApply = () => {
        setSelectedTagIds(draftTagIds);
        setIsOpened(false);
    };

    return (
        <div className="HomeReportsTagsFilter">
            <SelectComponent
                className="no-rem"
                isOpened={isOpened}
                setIsOpened={setIsOpened}
                thumbnailText={<div style={{ opacity: 0.8 }}>Tags</div>}
                fieldProps={{ style: { minWidth: 200 } }}
                dropdownProps={{
                    isOpened: isOpened,
                    place: "bottom-start",
                    className: "HomeReportsTagsFilter__dropdown",
                    style: {
                        overflowX: "hidden",
                        display: 'block',
                        width: 280,
                        padding: 0,
                    },
                }}
            >
                <div className="HomeReportsTagsFilter__dropdown-content">
                    <label className="HomeReportsTagsFilter__search">
                        <span className="HomeReportsTagsFilter__search-icon">
                            <FiSearch />
                        </span>
                        <TextInput
                            placeholder="Search"
                            value={search}
                            setValue={setSearch}
                            autoFocus
                        />
                    </label>
                    <div className="HomeReportsTagsFilter__options-dark">
                        <CustomBaseScrollbars autoHeight autoHeightMax={240}>
                            <div className="HomeReportsTagsFilter__options">
                                {filteredTags.map(tag => (
                                    <div key={tag.id} className="HomeReportsTagsFilter__row">
                                        <Toggle
                                            isChecked={draftTagIds.includes(tag.id)}
                                            onToggle={() => setDraftTagIds(toggleInArray(draftTagIds, tag.id))}
                                        >
                                            <span dangerouslySetInnerHTML={{
                                                __html: markMatch(tag.value, search.trim()),
                                            }} />
                                        </Toggle>
                                    </div>
                                ))}
                                {search.trim() !== '' && filteredTags.length === 0 && (
                                    <div className="HomeReportsTagsFilter__no-results">
                                        Nothing found.<br />Try searching something else.
                                    </div>
                                )}
                            </div>
                        </CustomBaseScrollbars>
                    </div>
                    <div className="HomeReportsTagsFilter__selected-bar">
                        <span className="HomeReportsTagsFilter__count">
                            {draftTagIds.length} selected
                        </span>
                        <button
                            type="button"
                            className="HomeReportsTagsFilter__reset-btn"
                            onClick={() => setDraftTagIds([])}
                            disabled={draftTagIds.length === 0}
                        >
                            Clear all
                        </button>
                    </div>
                    <div className="HomeReportsTagsFilter__footer">
                        <button
                            type="button"
                            className="HomeReportsTagsFilter__cancel-btn"
                            onClick={() => setIsOpened(false)}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="HomeReportsTagsFilter__apply-btn"
                            onClick={handleApply}
                        >
                            Apply
                        </button>
                    </div>
                </div>
            </SelectComponent>
            {selectedTags.length > 0 && (
                <div className="HomeReportsTagsFilter__chips">
                    {selectedTags.map(tag => (
                        <div key={tag.id} className="HomeReportsTagsFilter__chip">
                            <span className="HomeReportsTagsFilter__chip-label">{tag.value}</span>
                            <span
                                className="HomeReportsTagsFilter__chip-remove"
                                onClick={() => setSelectedTagIds(selectedTagIds.filter(id => id !== tag.id))}
                                aria-label={`Remove ${tag.value}`}
                            >
                                <IoClose />
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default HomeReportsTagsFilter;
